import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import axios from 'axios';
import { AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { config } from '@/config';
import { useAuth } from '@/hooks/useAuth';
import { StepUpChallenge } from '@/features/auth/StepUpChallenge';
import { isStepUpRequired, useStepUp } from '@/features/auth/stepUp';
import { AuthShell } from './signup/AuthShell';
import { ContinueAsAnotherAccount } from '@/components/auth/ContinueAsAnotherAccount';

/**
 * The app's half of the console handoff (`docs/WebAuthn.md` §4.7).
 *
 * The console has no relying party of its own, so staff prove presence here — where the second
 * factor lives — and carry the result across on a single-use ticket. That makes this page the
 * flow's one human checkpoint, which is why it asks rather than redirecting on arrival: a
 * handoff that completed silently would let any page that can navigate a browser mint a console
 * session from an already-signed-in tab.
 */
export default function ConsoleHandoff() {
  const [params] = useSearchParams();
  const { user, isLoading } = useAuth();
  const { challenge: stepUp, satisfy, clear } = useStepUp();
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const codeChallenge = params.get('challenge');
  const returnTo = params.get('returnTo') ?? '/';

  // Nothing is decided until the identity has actually loaded. Reading `user` while the query
  // is still in flight makes every arrival look signed-out for a frame, which here would mean
  // bouncing a signed-in admin to /signin.
  if (isLoading) {
    return (
      <AuthShell>
        <div className="flex items-center justify-center p-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </AuthShell>
    );
  }

  // ⚠ **Gated here rather than by `AuthOutlet`, and the query string is why.** `AuthOutlet`
  // redirects with a bare `<Navigate to="/signin">`, which drops the location it came from —
  // and this URL *is* the flow: lose `challenge` and the console is left waiting for a ticket
  // that can never be minted, with nothing on screen to say so. `/oauth/consent` sits outside
  // the outlet for exactly this reason.
  if (!user) {
    // ⚠ **Built from this route's own params, never from `window.location`.** Reading a mutable
    // global during render is not idempotent: once the router has begun moving to /signin, an
    // extra render computes `returnTo` from *that* address and bounces to
    // `/signin?returnTo=/signin?returnTo=…`, which drops the user on the sign-in page after
    // sign-in. It only shows up under load, which is where an e2e caught it. Same construction
    // as `/oauth/consent`, for the same reason.
    const returnTo = `/console-handoff?${params.toString()}`;
    return <Navigate to={`/signin?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  // Only staff can be handed a console. A non-admin gets the same nothing they would get at the
  // console itself, and the server refuses independently.
  if (!user.isAdmin) {
    return (
      <AuthShell>
        <div className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>This account cannot open the admin console.</AlertDescription>
          </Alert>
          <div className="mt-4">
            <ContinueAsAnotherAccount />
          </div>
        </div>
      </AuthShell>
    );
  }

  if (!codeChallenge) {
    return (
      <AuthShell>
        <div className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This link is incomplete. Start from the console rather than opening this page directly.
            </AlertDescription>
          </Alert>
        </div>
      </AuthShell>
    );
  }

  const confirm = () => {
    setError(undefined);
    setPending(true);
    void axios
      .post<{ ticket: string }>(
        `${config.backendApi}/api/auth/console-ticket`,
        { codeChallenge, returnTo },
        { withCredentials: true },
      )
      .then(
        ({ data }) => {
          if (!config.adminUrl) {
            setError('No console origin is configured for this deployment.');
            return;
          }
          // ⚠ **The ticket rides in the fragment, never the query.** Fragments are not sent to
          // servers and do not appear in `Referer`; this value converts directly into a console
          // session for its thirty seconds, so a query parameter would write it into the
          // console host's access logs.
          window.location.assign(`${config.adminUrl}/console-handoff#t=${encodeURIComponent(data.ticket)}`);
        },
        (e: unknown) => {
          const c = isStepUpRequired(e);
          if (c) { satisfy(c); return; }
          setError('Could not start the handoff. Try again from the console.');
        },
      )
      .finally(() => { setPending(false); });
  };

  return (
    <AuthShell>
      <div className="space-y-4 p-6">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Open the admin console?</h2>
          <p className="text-sm text-muted-foreground">
            You will be signed in to the console as {user.email}. This is a separate site from your
            normal account.
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {stepUp && (
          <StepUpChallenge
            challenge={stepUp}
            onSatisfied={() => { clear(); confirm(); }}
            onCancel={clear}
          />
        )}

        <Button className="w-full" onClick={confirm} disabled={pending} data-testid="confirm-handoff">
          {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
          {pending ? 'Preparing…' : 'Continue to the console'}
        </Button>
        <ContinueAsAnotherAccount />
      </div>
    </AuthShell>
  );
}
